import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { format, addDays, isBefore, startOfDay } from "date-fns";
import { 
  Calendar as CalendarIcon, 
  Clock, 
  ArrowLeft, 
  Check,
  User,
  Mail,
  Briefcase
} from "lucide-react";
import { Calendar } from "../components/ui/calendar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { API, useAuth } from "../App";

const BookingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);
  
  const [formData, setFormData] = useState({
    name: user?.name || "",
    email: user?.email || "",
    service_type: ""
  });

  const services = [
    { value: "custom-ai", label: "Custom AI Solutions" },
    { value: "lead-gen", label: "Lead Generation Systems" },
    { value: "automation", label: "Workflow Automation" },
    { value: "consultation", label: "General Consultation" }
  ];

  // Fetch available slots when date is selected
  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots(format(selectedDate, "yyyy-MM-dd"));
    }
  }, [selectedDate]);

  // Update form when user logs in
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || prev.name,
        email: user.email || prev.email
      }));
    }
  }, [user]);

  const fetchAvailableSlots = async (date) => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/bookings/available-slots?date=${date}`);
      const data = await response.json();
      setAvailableSlots(data.available_slots || []);
    } catch (error) {
      console.error("Error fetching slots:", error);
      toast.error("Failed to load available slots");
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate || !selectedSlot) {
      toast.error("Please select a date and time");
      return;
    }
    
    if (!formData.name || !formData.email || !formData.service_type) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    
    try {
      const response = await fetch(`${API}/bookings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include",
        body: JSON.stringify({
          ...formData,
          date: format(selectedDate, "yyyy-MM-dd"),
          time_slot: selectedSlot
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to create booking");
      }

      const booking = await response.json();
      setBookingDetails(booking);
      setBookingComplete(true);
      
      // Confetti celebration!
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
      
      toast.success("Booking confirmed!");
    } catch (error) {
      console.error("Booking error:", error);
      toast.error(error.message || "Failed to create booking");
    }
    
    setLoading(false);
  };

  const disabledDays = (date) => {
    const today = startOfDay(new Date());
    return isBefore(date, today) || date.getDay() === 0 || date.getDay() === 6;
  };

  if (bookingComplete && bookingDetails) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6" data-testid="booking-success">
        <motion.div 
          className="max-w-md w-full bg-white rounded-3xl p-8 border border-gray-200 shadow-xl text-center"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h1 
            className="text-2xl font-bold text-[#1A1A1A] mb-2"
            style={{ fontFamily: 'Syne, sans-serif' }}
            data-testid="success-title"
          >
            Booking Confirmed!
          </h1>
          <p className="text-[#64748B] mb-6">
            We've sent a confirmation email to {formData.email}
          </p>
          
          <div className="bg-[#F8F9FA] rounded-2xl p-6 text-left mb-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <CalendarIcon className="w-5 h-5 text-[#FF4F00]" />
                <div>
                  <p className="text-sm text-[#64748B]">Date</p>
                  <p className="font-medium">{format(selectedDate, "EEEE, MMMM d, yyyy")}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-[#FF4F00]" />
                <div>
                  <p className="text-sm text-[#64748B]">Time</p>
                  <p className="font-medium">{selectedSlot} (30 minutes)</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Briefcase className="w-5 h-5 text-[#FF4F00]" />
                <div>
                  <p className="text-sm text-[#64748B]">Service</p>
                  <p className="font-medium">
                    {services.find(s => s.value === formData.service_type)?.label}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col gap-3">
            <Button 
              className="btn-primary w-full"
              onClick={() => navigate("/")}
              data-testid="back-home-btn"
            >
              Back to Home
            </Button>
            {user && (
              <Button 
                variant="outline"
                className="w-full"
                onClick={() => navigate("/dashboard")}
                data-testid="view-bookings-btn"
              >
                View My Bookings
              </Button>
            )}
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="booking-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-[#64748B] hover:text-[#1A1A1A] transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <a href="/" className="flex items-center space-x-2">
            <span className="font-bold text-xl text-[#FF4F00]" style={{ fontFamily: 'Syne, sans-serif' }}>
              Yantra
            </span>
            <span className="font-bold text-xl text-[#1A1A1A]" style={{ fontFamily: 'Syne, sans-serif' }}>
              AI
            </span>
          </a>
          <div className="w-20" /> {/* Spacer for centering */}
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-24">
          {/* Left - Value Proposition */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="hidden lg:block"
          >
            <span className="text-[#FF4F00] font-medium text-sm uppercase tracking-wider">
              Discovery Call
            </span>
            <h1 
              className="text-4xl font-bold text-[#1A1A1A] mt-4 mb-6"
              style={{ fontFamily: 'Syne, sans-serif' }}
            >
              Let's Discuss Your AI Strategy
            </h1>
            <p className="text-[#64748B] text-lg mb-8">
              Book a free 30-minute call with our team to explore how AI can transform your business operations.
            </p>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#FF4F00]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Check className="w-5 h-5 text-[#FF4F00]" />
                </div>
                <div>
                  <h3 className="font-medium text-[#1A1A1A] mb-1">Understand Your Needs</h3>
                  <p className="text-[#64748B] text-sm">We'll learn about your business challenges and goals.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#FF4F00]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Check className="w-5 h-5 text-[#FF4F00]" />
                </div>
                <div>
                  <h3 className="font-medium text-[#1A1A1A] mb-1">Explore Solutions</h3>
                  <p className="text-[#64748B] text-sm">Discuss AI-powered solutions tailored to your needs.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#FF4F00]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Check className="w-5 h-5 text-[#FF4F00]" />
                </div>
                <div>
                  <h3 className="font-medium text-[#1A1A1A] mb-1">Get a Roadmap</h3>
                  <p className="text-[#64748B] text-sm">Receive actionable next steps and recommendations.</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right - Booking Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white lg:bg-[#F8F9FA] rounded-3xl lg:p-8"
          >
            <div className="lg:hidden mb-8">
              <h1 
                className="text-2xl font-bold text-[#1A1A1A] mb-2"
                style={{ fontFamily: 'Syne, sans-serif' }}
              >
                Book a Discovery Call
              </h1>
              <p className="text-[#64748B]">30 minutes • Free</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Step 1: Select Date & Time */}
              <div className="space-y-4">
                <h3 className="font-semibold text-[#1A1A1A] flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5 text-[#FF4F00]" />
                  Select Date & Time
                </h3>
                
                <div className="bg-white rounded-2xl p-4 border border-gray-100">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={disabledDays}
                    className="mx-auto"
                    data-testid="booking-calendar"
                    classNames={{
                      day_selected: "bg-[#FF4F00] text-white hover:bg-[#FF4F00] hover:text-white focus:bg-[#FF4F00] focus:text-white",
                      day_today: "bg-gray-100 text-[#1A1A1A]"
                    }}
                  />
                </div>

                {selectedDate && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="space-y-3"
                  >
                    <p className="text-sm font-medium text-[#64748B]">
                      Available times for {format(selectedDate, "MMMM d, yyyy")}
                    </p>
                    {loading ? (
                      <div className="flex justify-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#FF4F00]"></div>
                      </div>
                    ) : availableSlots.length > 0 ? (
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="time-slots">
                        {availableSlots.map((slot) => (
                          <button
                            key={slot}
                            type="button"
                            onClick={() => setSelectedSlot(slot)}
                            className={`time-slot ${selectedSlot === slot ? 'selected' : ''}`}
                            data-testid={`time-slot-${slot}`}
                          >
                            {slot}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-[#64748B] text-center py-4">
                        No available slots for this date
                      </p>
                    )}
                  </motion.div>
                )}
              </div>

              {/* Step 2: Your Details */}
              <div className="space-y-4 pt-4 border-t border-gray-200">
                <h3 className="font-semibold text-[#1A1A1A] flex items-center gap-2">
                  <User className="w-5 h-5 text-[#FF4F00]" />
                  Your Details
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="name" className="text-sm font-medium text-[#64748B]">
                      Full Name
                    </Label>
                    <Input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="John Doe"
                      className="input-field mt-1"
                      required
                      data-testid="booking-name-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="email" className="text-sm font-medium text-[#64748B]">
                      Email Address
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="john@example.com"
                      className="input-field mt-1"
                      required
                      data-testid="booking-email-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="service" className="text-sm font-medium text-[#64748B]">
                      Service Interest
                    </Label>
                    <Select 
                      value={formData.service_type}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, service_type: value }))}
                    >
                      <SelectTrigger 
                        className="input-field mt-1"
                        data-testid="booking-service-select"
                      >
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map((service) => (
                          <SelectItem key={service.value} value={service.value}>
                            {service.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="btn-primary w-full"
                disabled={loading || !selectedDate || !selectedSlot}
                data-testid="submit-booking-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Confirming...
                  </span>
                ) : (
                  "Confirm Booking"
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default BookingPage;
